import { Plus } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Loader } from "@/components/common/loader";
import { listFinancialStatements } from "@/features/bookkeeping/api";
import { AddStatementDialog } from "@/features/bookkeeping/components/add-statement-dialog";
import {
  STATEMENT_TYPE_LABEL,
  type FinancialStatement,
} from "@/features/bookkeeping/types";
import { ApiError } from "@/lib/api";
import { formatShortDate } from "@/lib/format";

function errText(err: unknown, fallback: string): string {
  return err instanceof ApiError && err.message ? err.message : fallback;
}

const COL_COUNT = 5;

export function FinancialsPanel({ clientId }: { clientId: string }) {
  const [statements, setStatements] = useState<FinancialStatement[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addOpen, setAddOpen] = useState(false);

  const mountedRef = useRef(true);
  const requestIdRef = useRef(0);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(() => {
    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);
    listFinancialStatements(clientId)
      .then((rows) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setStatements(rows);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setStatements([]);
        setError(errText(err, "Failed to load financial statements."));
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [clientId]);

  useEffect(() => {
    load();
  }, [load]);

  function handleCreated(statement: FinancialStatement) {
    setStatements((prev) => [statement, ...prev]);
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col justify-between gap-4 md:flex-row md:items-end">
        <div>
          <h1 className="text-foreground mb-2 text-3xl font-bold">Financials</h1>
          <p className="text-muted-foreground max-w-2xl">
            Financial statements prepared for this client from their bookkeeping data.
          </p>
        </div>
        <Button size="sm" onClick={() => setAddOpen(true)}>
          <Plus /> Add statement
        </Button>
      </div>

      <Card className="gap-0 overflow-hidden p-0">
        <Table>
          <TableHeader>
            <TableRow className="bg-muted/40">
              <TableHead>Period</TableHead>
              <TableHead>Type</TableHead>
              <TableHead>Summary</TableHead>
              <TableHead>Prepared by</TableHead>
              <TableHead>Created</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT} className="py-10">
                  <Loader />
                </TableCell>
              </TableRow>
            ) : error ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT} className="text-destructive text-center">
                  {error}
                </TableCell>
              </TableRow>
            ) : statements.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COL_COUNT} className="text-muted-foreground text-center">
                  No financial statements yet. Add one to get started.
                </TableCell>
              </TableRow>
            ) : (
              statements.map((statement) => (
                <TableRow key={statement.id}>
                  <TableCell className="text-foreground font-medium">
                    {statement.periodLabel}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary">
                      {STATEMENT_TYPE_LABEL[statement.statementType]}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-muted-foreground max-w-xs">
                    {statement.summary || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {statement.preparedBy || "—"}
                  </TableCell>
                  <TableCell className="text-muted-foreground">
                    {formatShortDate(statement.createdAt)}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      <AddStatementDialog
        open={addOpen}
        onOpenChange={setAddOpen}
        clientId={clientId}
        onCreated={handleCreated}
      />
    </div>
  );
}
